/**
 * MVP module — category weight resolution.
 * Source: Traditional guide Appendix B (docs/reference/mvp/
 * traditional-mips-scoring-methodology-py2026.md §6.1–6.3). Tables transcribed
 * verbatim; do not "fix" values without the policy doc in hand.
 *
 * Table 2 (small practice) differs from Table 1 in EXACTLY two rows:
 * No-PI and No-Cost+No-PI redistribute to Quality AND IA.
 *
 * The single-category-at-100% override (final score = performance threshold) is
 * exposed as `isSingleCategoryTerminal` — the engine applies it BEFORE category math.
 */

export interface WeightSet {
  quality: number;
  cost: number;
  ia: number;
  pi: number;
}

export type ReweightedCategory = 'quality' | 'cost' | 'ia' | 'pi';

function key(reweighted: Set<ReweightedCategory>): string {
  return ['quality', 'cost', 'ia', 'pi'].filter(c => reweighted.has(c as ReweightedCategory)).join('+') || 'none';
}

/** Appendix B Table 1 — standard participants (excluding small practices). */
const TABLE_1: Record<string, WeightSet> = {
  'none':        { quality: 30, cost: 30, ia: 15, pi: 25 },
  'cost':        { quality: 55, cost: 0,  ia: 15, pi: 30 },
  'pi':          { quality: 55, cost: 30, ia: 15, pi: 0 },
  'quality':     { quality: 0,  cost: 30, ia: 15, pi: 55 },
  'ia':          { quality: 45, cost: 30, ia: 0,  pi: 25 },
  'cost+pi':     { quality: 85, cost: 0,  ia: 15, pi: 0 },
  'quality+cost': { quality: 0, cost: 0,  ia: 15, pi: 85 },
  'cost+ia':     { quality: 70, cost: 0,  ia: 0,  pi: 30 },
  'quality+pi':  { quality: 0,  cost: 50, ia: 50, pi: 0 },
  'ia+pi':       { quality: 70, cost: 30, ia: 0,  pi: 0 },
  'quality+ia':  { quality: 0,  cost: 30, ia: 0,  pi: 70 },
};

/** Appendix B Table 2 — small practices. Differs from Table 1 in exactly two rows. */
const TABLE_2: Record<string, WeightSet> = {
  ...TABLE_1,
  'pi':      { quality: 40, cost: 30, ia: 30, pi: 0 },
  'cost+pi': { quality: 50, cost: 0,  ia: 50, pi: 0 },
};

export interface ResolvedWeights {
  weights: WeightSet;
  weightSetKey: string;               // audit: which table + row scored this pathway
  /** ≥3 categories reweighted leaving one at 100% → final score = threshold (Trad §6.3). */
  isSingleCategoryTerminal: boolean;
}

export function resolveWeights(
  isSmallPractice: boolean,
  reweighted: Set<ReweightedCategory>
): ResolvedWeights {
  const table = isSmallPractice ? TABLE_2 : TABLE_1;
  const k = key(reweighted);
  const tableName = isSmallPractice ? 'T2' : 'T1';

  const fromTable = table[k];
  if (fromTable) {
    const active = (['quality', 'cost', 'ia', 'pi'] as const).filter(c => fromTable[c] > 0);
    return {
      weights: fromTable,
      weightSetKey: `${tableName}:${k}`,
      isSingleCategoryTerminal: active.length === 1,
    };
  }

  // 3+ categories reweighted — beyond the published tables. Whatever survives
  // carries 100%: the single-category terminal rule fires (Trad §6.3).
  const all: ReweightedCategory[] = ['quality', 'cost', 'ia', 'pi'];
  const surviving = all.filter(c => !reweighted.has(c));
  const weights: WeightSet = { quality: 0, cost: 0, ia: 0, pi: 0 };
  surviving.forEach(c => { weights[c] = 100 / surviving.length; });
  return {
    weights,
    weightSetKey: `${tableName}:${k}(derived)`,
    isSingleCategoryTerminal: surviving.length <= 1,
  };
}
